/**
 * Learning Center API: the Team tab's reports (plan docs/plans/lms-plan.md
 * Appendix A "Reports"; 8.4, 11.3, 13 tab 6 "Matrix" and "Reports"; 18.23,
 * 18.27; WP-39's routes, WP-45's client). Typed calls over services/api (the
 * shared axios instance that adds the JWT and X-Site-Id); nothing under src/lms
 * imports axios (2.3a). Every route is an lms admin route.
 *
 *   GET /admin/matrix?site_id=&group_id=&status=&course_id=&limit=&offset=
 *       -> { site, rows: [{ user, cells }], courses, tiles, page }            (8.4, R16)
 *   GET /admin/matrix.csv (the same filters; every row they select)
 *       -> text/csv attachment, audited                                      (8.4, R16)
 *   GET /admin/reports/completion-rates?from=&to=&site_id=
 *       -> { from, to, timezone, range, sites, rows }  (from and to REQUIRED as YYYY-MM-DD, 18.23)
 *   GET /admin/reports/item-analysis/:versionId
 *       -> { version, course, attempts, questions }                          (R21)
 *   GET /admin/reports/campaigns/:id
 *       -> { campaign, outcomes, progress, completion_rate_pct, on_time_rate_pct,
 *            recipients, hidden_recipient_count }                            (8.2, R23)
 *   GET /admin/reports/sops/:id/versions/:vid/acknowledgements.csv
 *       -> text/csv attachment, audited                                      (11.3, R25)
 *
 * A CSV is a text/csv attachment streamed with no-store and audited before it
 * is sent (18.23): it is fetched as a Blob (axios responseType 'blob') and
 * saved through an object URL and a download anchor, the way WP-41's training
 * log is (api/certificates.ts saveBlob). A failed CSV request answers a Blob
 * holding the JSON { error, code }; readBlobError turns it back into that body
 * so api/client.ts apiErrorMessage can read it.
 *
 * Query strings travel as axios `params`, so the URL itself stays the path
 * (the test harness routes GETs by exact URL).
 */
import api from '../../services/api';
import type { ComplianceStatus } from '../lib/complianceStatus';
import type { CampaignKind, CampaignOutcome, CampaignOutcomeCounts } from './campaigns';
import { filenameFromDisposition, readBlobError } from './certificates';

const enc = encodeURIComponent;

// ---------------------------------------------------------------------------
// Files (the CSV answers)
// ---------------------------------------------------------------------------

/** A downloaded CSV: the bytes and the name the server gave it. */
export interface CsvFile {
  blob: Blob;
  filename: string;
}

/** GET a text/csv attachment as a Blob (see the file header). */
async function getCsv(url: string, params: Record<string, string | number>, fallbackName: string): Promise<CsvFile> {
  try {
    const response = await api.get<Blob>(url, { params, responseType: 'blob' });
    const headers = (response as { headers?: Record<string, unknown> }).headers || {};
    const data = response.data;
    const blob = data instanceof Blob ? data : new Blob([data as BlobPart], { type: 'text/csv' });
    return { blob, filename: filenameFromDisposition(headers['content-disposition']) || fallbackName };
  } catch (err) {
    throw await readBlobError(err);
  }
}

/** A local calendar day as YYYY-MM-DD (the completion-rates range is sent this way, 18.23). */
export function toDayString(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** True for a real calendar day written YYYY-MM-DD. */
export function isDayString(value: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return false;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.getFullYear() === Number(m[1]) && d.getMonth() === Number(m[2]) - 1 && d.getDate() === Number(m[3]);
}

// ---------------------------------------------------------------------------
// The compliance matrix (8.4)
// ---------------------------------------------------------------------------

/** services/lms/compliance/matrix.js: the default page and the Appendix A cap. */
export const MATRIX_PAGE_LIMIT = 100;
export const MATRIX_MAX_LIMIT = 200;

/** 8.4: the expiring tile counts completions expiring within this many days. */
export const EXPIRING_TILE_DAYS = 30;

/**
 * One (person, course) cell: computeStatus's result (8.4; the GET /me/status
 * cell). The ids let the Team tab waive from a cell (18.16): assignment_id is
 * the OPEN assignment for overdue, assigned and a compliant or expiring cell
 * with an open renewal, and the WAIVED assignment for a waived cell.
 */
export interface MatrixCell {
  status: ComplianceStatus;
  due_at?: string;
  expires_at?: string;
  assignment_id?: string;
  completion_id?: string;
}

export interface MatrixRow {
  user: { id: string; full_name: string | null; email: string | null };
  cells: Record<string, MatrixCell>;
}

/** A column: a course required of, held by or assigned to someone listed (8.4). */
export interface MatrixCourse {
  id: string;
  title: string;
  slug: string;
  category: string;
  validity_months: number | null;
}

/** 8.4 "the counts as tiles": compliant (and expiring), expiring in 30 days, overdue, missing. */
export interface MatrixTiles {
  compliant: number;
  expiring: number;
  overdue: number;
  missing: number;
}

export interface MatrixReport {
  site: { id: string; name: string | null };
  rows: MatrixRow[];
  courses: MatrixCourse[];
  tiles: MatrixTiles;
  page: { limit: number; offset: number; hasMore: boolean; total: number };
}

/** The Appendix A filters (every one optional; the site defaults to X-Site-Id's). */
export interface MatrixQuery {
  siteId?: string | null;
  groupId?: string | null;
  status?: ComplianceStatus | null;
  courseId?: string | null;
  limit?: number;
  offset?: number;
}

function matrixFilterParams(q: MatrixQuery): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  if (q.siteId) params.site_id = q.siteId;
  if (q.groupId) params.group_id = q.groupId;
  if (q.status) params.status = q.status;
  if (q.courseId) params.course_id = q.courseId;
  return params;
}

/** GET /api/lms/admin/matrix */
export async function getMatrix(q: MatrixQuery = {}): Promise<MatrixReport> {
  const params = matrixFilterParams(q);
  params.limit = Math.min(q.limit ?? MATRIX_PAGE_LIMIT, MATRIX_MAX_LIMIT);
  params.offset = q.offset ?? 0;
  const response = await api.get<MatrixReport>('/api/lms/admin/matrix', { params });
  return response.data;
}

/** GET /api/lms/admin/matrix.csv: every row the filters select (paging is the screen's, 18.23). */
export async function downloadMatrixCsv(q: MatrixQuery = {}): Promise<CsvFile> {
  return getCsv('/api/lms/admin/matrix.csv', matrixFilterParams(q), `lms-compliance-matrix-${toDayString(new Date())}.csv`);
}

// ---------------------------------------------------------------------------
// Completion rates per course and site (13 tab 6 Reports; 18.23)
// ---------------------------------------------------------------------------

/** The counts of one course (or one course at one site) with its two rates (floored integers, null over nothing). */
export interface RateCounts {
  assigned: number;
  completed: number;
  completed_on_time: number;
  open: number;
  overdue: number;
  waived: number;
  cancelled: number;
  completions: number;
  completion_rate_pct: number | null;
  on_time_rate_pct: number | null;
}

export interface CompletionRateRow extends RateCounts {
  course: { id: string; title: string; slug: string; category: string; is_active: boolean };
  by_site: Array<RateCounts & { site_id: string }>;
}

export interface CompletionRates {
  from: string;
  to: string;
  timezone: string;
  range: { from_at: string; to_before: string };
  sites: Array<{ id: string; name: string | null; people: number }>;
  rows: CompletionRateRow[];
}

export interface CompletionRatesQuery {
  /** YYYY-MM-DD in the network's time zone; required (the route answers 400 without it). */
  from: string;
  to: string;
  siteId?: string | null;
}

/** GET /api/lms/admin/reports/completion-rates */
export async function getCompletionRates(q: CompletionRatesQuery): Promise<CompletionRates> {
  const params: Record<string, string> = { from: q.from, to: q.to };
  if (q.siteId) params.site_id = q.siteId;
  const response = await api.get<CompletionRates>('/api/lms/admin/reports/completion-rates', { params });
  return response.data;
}

// ---------------------------------------------------------------------------
// Item analysis of one course version (R21)
// ---------------------------------------------------------------------------

export interface ItemOption {
  id: string;
  text: string;
  correct: boolean;
  /** How many submitted attempts selected this option. */
  chosen: number;
}

export interface ItemQuestion {
  id: string;
  ordinal: number;
  kind: 'single' | 'multi' | 'true_false';
  stem: string;
  section_id: string;
  difficulty: string | null;
  objective_index: number | null;
  /** Submitted attempts that drew the question. */
  shown: number;
  answered: number;
  correct: number;
  missed: number;
  /** missed over shown, a floored integer percentage; null when never shown. */
  miss_rate: number | null;
  options: ItemOption[];
}

export interface ItemAnalysis {
  version: { id: string; course_id: string; version_no: number; status: string };
  course: { id: string; title: string; slug: string };
  /** out_of_reach counts attempts by people the admin does not reach (left out, never named; 18.23). */
  attempts: { submitted: number; passed: number; failed: number; out_of_reach: number };
  questions: ItemQuestion[];
}

/** GET /api/lms/admin/reports/item-analysis/:versionId */
export async function getItemAnalysis(versionId: string): Promise<ItemAnalysis> {
  const response = await api.get<ItemAnalysis>(`/api/lms/admin/reports/item-analysis/${enc(versionId)}`);
  return response.data;
}

// ---------------------------------------------------------------------------
// A campaign's outcomes and where each person stands (8.2, R23)
// ---------------------------------------------------------------------------

/** compliance/reports.js RECIPIENT_STATUSES. */
export type RecipientStatus =
  | 'completed'
  | 'open'
  | 'overdue'
  | 'waived'
  | 'cancelled'
  | 'already_current'
  | 'skipped_inactive'
  | 'no_assignment';

export const RECIPIENT_STATUSES: readonly RecipientStatus[] = [
  'completed',
  'open',
  'overdue',
  'waived',
  'cancelled',
  'already_current',
  'skipped_inactive',
  'no_assignment',
];

export interface CampaignReportRecipient {
  user_id: string;
  full_name: string | null;
  email: string | null;
  deleted: boolean;
  outcome: CampaignOutcome;
  status: RecipientStatus;
  on_time: boolean | null;
  assignment: {
    id: string;
    status: string;
    source: string;
    due_at: string;
    completed_at: string | null;
    completion_id: string | null;
  } | null;
}

export interface CampaignReport {
  campaign: {
    id: string;
    kind: CampaignKind;
    course: { id: string; title: string; slug: string } | null;
    course_version_id: string | null;
    site_id: string | null;
    message: string | null;
    due_days: number;
    due_at: string;
    force: boolean;
    recipient_count: number;
    sent_at: string;
    sent_by: { id: string; full_name: string | null };
  };
  outcomes: CampaignOutcomeCounts;
  progress: Record<RecipientStatus, number> & { completed_on_time: number };
  completion_rate_pct: number | null;
  on_time_rate_pct: number | null;
  recipients: CampaignReportRecipient[];
  /** Recipients the viewing admin does not reach: counted, never named (G14, 18.21). */
  hidden_recipient_count: number;
}

/** GET /api/lms/admin/reports/campaigns/:id */
export async function getCampaignReport(campaignId: string): Promise<CampaignReport> {
  const response = await api.get<CampaignReport>(`/api/lms/admin/reports/campaigns/${enc(campaignId)}`);
  return response.data;
}

// ---------------------------------------------------------------------------
// The SOP acknowledgement CSV (11.3; the tracker's one definition, 18.27)
// ---------------------------------------------------------------------------

/** GET /api/lms/admin/reports/sops/:id/versions/:vid/acknowledgements.csv */
export async function downloadSopAcknowledgementsCsv(sopId: string, versionId: string): Promise<CsvFile> {
  return getCsv(
    `/api/lms/admin/reports/sops/${enc(sopId)}/versions/${enc(versionId)}/acknowledgements.csv`,
    {},
    `lms-sop-acknowledgements-${toDayString(new Date())}.csv`
  );
}
